/**
 * Celery Job Service
 * Handles communication with Django Celery API for background tasks
 */

// Use relative URL to go through partner.mhsonline.au with valid SSL
const DJANGO_API_URL = process.env.REACT_APP_API_BASE_URL || '';

class CeleryJobService {
  /**
   * Trigger order sync job
   * @param {string} jobType - 'full-sync', 'single-order', or 'customer-orders'
   * @param {object} options - Additional options (customerId, orderId, etc.)
   * @returns {Promise<{taskId: string, status: string}>}
   */
  async triggerOrderSync(jobType = 'full-sync', options = {}) {
    try {
      const response = await fetch(`${DJANGO_API_URL}/api/sync/orders/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          job_type: jobType,
          customer_id: options.customerId,
          order_id: options.orderId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger sync: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        taskId: data.task_id,
        status: data.status,
        message: data.message
      };
    } catch (error) {
      console.error('Error triggering order sync:', error);
      throw error;
    }
  }

  /**
   * Get task status
   * @param {string} taskId - Celery task ID
   * @returns {Promise<object>} Task status and progress
   */
  async getTaskStatus(taskId) {
    try {
      const response = await fetch(`${DJANGO_API_URL}/api/async/tasks/${taskId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get task status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting task status:', error);
      throw error;
    }
  }

  /**
   * Poll task status until completion
   * @param {string} taskId - Celery task ID
   * @param {function} onProgress - Callback for progress updates
   * @param {function} onComplete - Callback when task completes
   * @param {function} onError - Callback on error
   * @param {number} pollInterval - Polling interval in ms (default: 5000 = 5 seconds)
   */
  async pollTaskStatus(taskId, onProgress, onComplete, onError, pollInterval = 5000) {
    const poll = async () => {
      try {
        const status = await this.getTaskStatus(taskId);

        // Handle different task states
        switch (status.state) {
          case 'PENDING':
            onProgress({
              progress: 0,
              message: status.message || 'Task is waiting to start...',
              state: 'PENDING'
            });
            setTimeout(poll, pollInterval);
            break;

          case 'STARTED':
            onProgress({
              progress: 0,
              message: status.message || 'Task has started...',
              state: 'STARTED'
            });
            setTimeout(poll, pollInterval);
            break;

          case 'PROGRESS':
            onProgress({
              progress: status.progress || 0,
              current: status.current,
              total: status.total,
              message: status.message || 'Processing...',
              state: 'PROGRESS'
            });
            setTimeout(poll, pollInterval);
            break;

          case 'SUCCESS':
            onComplete({
              result: status.result,
              message: status.message || 'Task completed successfully',
              state: 'SUCCESS'
            });
            break;

          case 'FAILURE':
            onError({
              error: status.error || 'Task failed',
              message: status.message || 'An error occurred',
              state: 'FAILURE'
            });
            break;

          case 'RETRY':
            onProgress({
              progress: 0,
              message: status.message || 'Task is being retried...',
              state: 'RETRY'
            });
            setTimeout(poll, pollInterval);
            break;

          default:
            // Unknown state, keep polling
            setTimeout(poll, pollInterval);
        }
      } catch (error) {
        onError({
          error: error.message,
          message: 'Failed to get task status',
          state: 'ERROR'
        });
      }
    };

    // Start polling
    poll();
  }

  /**
   * List recent tasks
   * @param {number} limit - Number of tasks to retrieve
   * @param {string} status - Filter by status (optional)
   * @returns {Promise<Array>} List of recent tasks
   */
  async listRecentTasks(limit = 20, status = null) {
    try {
      const url = new URL(`${DJANGO_API_URL}/api/async/tasks/`, DJANGO_API_URL || window.location.origin);
      url.searchParams.append('limit', limit);
      if (status) {
        url.searchParams.append('status', status);
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to list tasks: ${response.statusText}`);
      }

      const data = await response.json();
      return data.tasks || [];
    } catch (error) {
      // Degrade gracefully: the job-history widget should render empty rather
      // than crash if the Celery/Django API host isn't configured or reachable.
      console.error('Error listing tasks:', error);
      return [];
    }
  }

  /**
   * Cancel a running task
   * @param {string} taskId - Celery task ID
   * @returns {Promise<object>} Cancellation result
   */
  async cancelTask(taskId) {
    try {
      const response = await fetch(`${DJANGO_API_URL}/api/async/tasks/${taskId}/cancel/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel task: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling task:', error);
      throw error;
    }
  }

  /**
   * Trigger part search task
   * @param {string} searchTerm - Part number or description
   * @param {string} searchType - 'exact', 'fuzzy', or 'description'
   * @param {string} accountId - Customer UUID (optional)
   * @param {boolean} includeStock - Include stock/pricing data
   * @returns {Promise<{taskId: string, status: string}>}
   */
  async triggerPartSearch(searchTerm, searchType = 'exact', accountId = null, includeStock = true) {
    try {
      const url = new URL(`${DJANGO_API_URL}/api/async/part-search/`, DJANGO_API_URL || window.location.origin);
      url.searchParams.append('search_term', searchTerm);
      url.searchParams.append('search_type', searchType);
      if (accountId) {
        url.searchParams.append('account_id', accountId);
      }
      url.searchParams.append('include_stock', includeStock);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger search: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        taskId: data.task_id,
        status: data.status,
        message: data.message,
        searchTerm: data.search_term
      };
    } catch (error) {
      console.error('Error triggering part search:', error);
      throw error;
    }
  }

  /**
   * Get queue statistics
   * @returns {Promise<object>} Queue stats
   */
  async getQueueStats() {
    try {
      const response = await fetch(`${DJANGO_API_URL}/api/async/queue-stats/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get queue stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting queue stats:', error);
      throw error;
    }
  }

  /**
   * Get authentication token
   * @returns {string} Auth token
   */
  getAuthToken() {
    // Get token from your auth system
    // This is a placeholder - adjust based on your auth implementation
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    return token || '';
  }
}

// Export singleton instance
export const celeryJobService = new CeleryJobService();
export default celeryJobService;
